import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import RoleDefaultAccessContent from './components/RoleDefaultAccessContent';
import UserRolesManagementContent from './components/UserRolesManagementContent';
import ReferentDefinitionsContent from './components/ReferentDefinitionsContent';
import InvitationCodesContent from './components/InvitationCodesContent';

interface CmManagementProps {
  /** Rendu dans le shell du panel v1 (sans Navbar / fond plein écran). */
  embedded?: boolean;
}

/**
 * Page Référent CM : regroupe « Accès par défaut » et « Gérer les rôles »
 * avec le même système d’onglets que la page Configuration.
 */
const CmManagement: React.FC<CmManagementProps> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'default-access' | 'roles' | 'referents' | 'invitation-codes'>('default-access');

  const hasAccess = currentUser?.roles_id && currentUser.roles_id <= 7;

  useEffect(() => {
    if (!hasAccess) {
      navigate('/panel');
    }
  }, [hasAccess, navigate]);

  if (!hasAccess) {
    return null;
  }

  const header = embedded ? (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-xl font-bold text-cockpit-text">Référent CM</h1>
        <p className="mt-1 text-sm text-cockpit-text-2">
          Accès par défaut, gestion des rôles utilisateurs et définition des fonctions référent.
        </p>
      </div>
      <button
        type="button"
        onClick={() => navigate('/panel/cm')}
        className="shrink-0 rounded-lg border border-[#223046] bg-[#0b1220] px-4 py-2 text-sm font-semibold text-cockpit-text transition-colors hover:bg-[#121b2b]"
      >
        Retour espace CM
      </button>
    </div>
  ) : (
    <div className="mb-8 flex items-center justify-between">
      <div>
        <h1 className="text-5xl font-bold text-cockpit-text mb-2 text-cockpit-text">Référent CM</h1>
        <p className="text-cockpit-text-2">
          Accès par défaut, gestion des rôles utilisateurs et définition des fonctions référent.
        </p>
      </div>
      <button
        type="button"
        onClick={() => navigate('/panel')}
        className="flex items-center space-x-2 rounded-lg bg-[#6366f1] px-6 py-3 font-semibold text-cockpit-text shadow-lg transition-all hover:bg-[#5457e6] hover:shadow-xl"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span>Retour au panel</span>
      </button>
    </div>
  );

  const inner = (
    <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>
      {header}

      <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 p-6 backdrop-blur-sm">
            <div className="flex flex-wrap border-b border-[#1a2537] mb-6">
              {(['default-access', 'roles', 'referents', 'invitation-codes'] as const).map((tab) => {
                const labels: Record<string, string> = {
                  'default-access': 'Accès par défaut',
                  'roles': 'Gérer les rôles',
                  'referents': 'Gestion des référents',
                  'invitation-codes': "Codes d'invitation",
                };
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-5 py-3 text-sm font-medium transition-colors relative ${
                      activeTab === tab
                        ? 'text-[#a5b4fc] border-b-2 border-[#6366f1]'
                        : 'text-cockpit-text-2 hover:text-cockpit-text'
                    }`}
                  >
                    {labels[tab]}
                  </button>
                );
              })}
            </div>

            <div className="mt-6">
              {activeTab === 'default-access' && <RoleDefaultAccessContent />}
              {activeTab === 'roles' && <UserRolesManagementContent />}
              {activeTab === 'referents' && <ReferentDefinitionsContent />}
              {activeTab === 'invitation-codes' && <InvitationCodesContent />}
            </div>
          </div>
    </div>
  );

  if (embedded) {
    return <div className="text-cockpit-text">{inner}</div>;
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pb-16 pt-24">{inner}</div>
    </div>
  );
};

export default CmManagement;
